import { useEffect, useMemo, useRef, useState, SyntheticEvent } from "react";
import { collectFileUrls, FileContext, getFileUrl, storageObject, withFileUrls } from "../services/fileUrls";

export function useMessageFiles<T>(data: T, context: FileContext, enabled = true) {
  const serialized = JSON.stringify({ data, context, enabled });
  const input = useMemo(() => JSON.parse(serialized) as { data: T; context: FileContext; enabled: boolean }, [serialized]);
  const references = useMemo(() => collectFileUrls(input.data, input.context.bucket), [input]);
  const [state, setState] = useState({ key: "", urls: new Map<string, string>(), errors: new Map<string, string>() });
  const [retry, setRetry] = useState(0);
  const lastRetry = useRef({ key: "", at: 0 });
  const playback = useRef(new Map<HTMLMediaElement, { time: number; playing: boolean }>());

  useEffect(() => {
    if (!input.enabled || !references.length) return;
    let cancelled = false;
    const urls = new Map<string, string>();
    const errors = new Map<string, string>();
    Promise.all(references.map(async (url) => {
      const object = storageObject(url, input.context.bucket)!;
      try {
        urls.set(object, await getFileUrl(url, input.context, retry > 0));
      } catch (error) { errors.set(object, error instanceof Error ? error.message : "File access unavailable"); }
    })).then(() => {
      if (!cancelled) setState({ key: serialized, urls, errors });
    });
    return () => { cancelled = true; };
  }, [input, references, retry, serialized]);

  const urls = state.key === serialized ? state.urls : new Map<string, string>();
  const currentUrl = (url: string): string => {
    const object = storageObject(url, context.bucket);
    return object === null || !enabled ? url : urls.get(object) || "";
  };
  const resolveUrl = async (url: string): Promise<string> => {
    if (!enabled) return url;
    const object = storageObject(url, context.bucket);
    try {
      const result = await getFileUrl(url, context);
      if (object !== null) setState((previous) => {
        if (previous.key !== serialized) return previous;
        const errors = new Map(previous.errors);
        errors.delete(object);
        return { ...previous, urls: new Map(previous.urls).set(object, result), errors };
      });
      return result;
    } catch (error) {
      if (object !== null) setState((previous) => previous.key === serialized ? {
        ...previous,
        errors: new Map(previous.errors).set(object, error instanceof Error ? error.message : "File access unavailable"),
      } : previous);
      throw error;
    }
  };

  const onError = (event: SyntheticEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement) && !(target instanceof HTMLMediaElement)) return;
    const object = storageObject(target.currentSrc || target.src, context.bucket);
    if (!enabled || object === null || !urls.has(object)
      || (lastRetry.current.key === serialized && Date.now() - lastRetry.current.at < 30000)) return;
    lastRetry.current = { key: serialized, at: Date.now() };
    if (target instanceof HTMLMediaElement) {
      playback.current.set(target, { time: target.currentTime, playing: !target.paused });
    }
    setRetry(value => value + 1);
  };
  const onLoadedMetadata = (event: SyntheticEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLMediaElement)) return;
    const previous = playback.current.get(target);
    if (!previous) return;
    playback.current.delete(target);
    if (Number.isFinite(previous.time)) target.currentTime = previous.time;
    if (previous.playing) void target.play().catch(() => {});
  };

  return {
    data: enabled ? withFileUrls(data, context.bucket, urls) : data,
    failed: enabled && state.key === serialized && state.errors.size > 0,
    error: enabled && state.key === serialized ? state.errors.values().next().value || "" : "",
    loading: enabled && references.length > 0 && state.key !== serialized,
    currentUrl, resolveUrl, onError, onLoadedMetadata,
  };
}
